import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { has } from "lodash";

export type RetryCondition<T> = (
    error: FetchBaseQueryError | undefined,
    result: T | undefined,
    attempt: number
) => boolean;

export async function retryFetch(
    fetchFn: () => Promise<any>,
    maxRetries: number = 3,
    retryCondition?: RetryCondition<any>
): Promise<any> {
    let attempt = 0;
    let lastError: FetchBaseQueryError | undefined = undefined;

    function serializeError(err?: FetchBaseQueryError | null) {
        if (!err) return { message: 'Unknown error' };
        const out: any = { message: (err as any).error ?? undefined };
        if ((err as any).status !== undefined) out.status = (err as any).status;
        if ((err as any).data !== undefined) out.data = (err as any).data;
        if (typeof out.message === 'object') out.message = JSON.stringify(out.message);
        return out;
    }

    while (attempt <= maxRetries) {
        try {
            const response = await fetchFn();

            if (has(response, 'error')) {
                lastError = response.error as FetchBaseQueryError;
                if (shouldRetry(lastError, null, retryCondition, attempt)) {
                    attempt++;
                    continue;
                }
                return { error: serializeError(lastError) };
            }

            if (shouldRetry(null, response, retryCondition, attempt)) {
                attempt++;
                continue;
            }
            return response;
        } catch (error) {
            lastError = error as FetchBaseQueryError;
            if (shouldRetry(lastError, null, retryCondition, attempt)) {
                attempt++;
                continue;
            }
            return { error: serializeError(lastError) };
        }
    }

    return { error: serializeError(lastError ?? null) };
}

export function shouldRetry<T>(
    error: FetchBaseQueryError | null,
    response: T | null,
    retryCondition: RetryCondition<T> | undefined,
    attempt: number
): boolean {
    if (typeof retryCondition === 'function') {
        return retryCondition(error ?? undefined, response ?? undefined, attempt);
    }
    if (error) {
        return (error.status === 504 ||
            error.status === 400 ||
            (typeof error.status === 'string' && error.status.toLocaleLowerCase().includes('timeout'))
        )
    }
    return false;
}